import { Component, EventEmitter, Inject, Input, OnInit, Output } from '@angular/core'
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms'
import { ActionFailed, compactObject, integer, isSomething, SaveForm, Toaster } from '@tokeet-frontend/tv3-platform'
import {
  BdcContentV2Service,
  BdcCreateUnitPayload,
  BdcRoomType,
  BdcSizeUnit,
  bdcSmokingPolicyOptions,
  BdcUnitMetadataBedTypeItem,
  BdcUnitMetadataUnitNameItem,
  BdcUnitMetadataUnitTypeItem,
  BdcUnitResponse,
  IBdcBed,
  IBdcBedConfiguration,
  IBdcRoom,
} from '@tokeet-frontend/channel-content'
import { Store } from '@ngrx/store'
import { finalize } from 'rxjs/operators'
import * as lodash from 'lodash'
import { MatSelectChange } from '@angular/material/select'

@Component({
  selector: 'app-bdc-property-unit-details',
  templateUrl: './bdc-property-unit-details.component.html',
})
export class BdcPropertyUnitDetailsComponent implements OnInit {
  @Input() propertyId: number
  @Input() propertyTypeId: number
  _unit: BdcUnitResponse
  @Input() set unit(u: BdcUnitResponse) {
    this._unit = u
    this.isEdit = !!u
    if (this.isEdit) {
      this.setForm()
    }
  }
  get unit() {
    return this._unit
  }

  @Output() close = new EventEmitter<void>()

  occupancyForm = this.fb.group({
    max_guests: [null, [Validators.min(1), Validators.max(50), integer()]],
    max_adults: [null, [Validators.min(1), Validators.max(50), integer()]],
    max_children: [null, [Validators.min(0), Validators.max(49), integer()]],
  })

  extraBedsForm = this.fb.group({
    extra_beds: [null, [Validators.min(0), Validators.max(100), integer()]],
    cribs: [null, [Validators.min(0), Validators.max(100), integer()]],
    is_crib_and_extra_bed_allowed: [],
  })

  sizeForm = this.fb.group({
    value: [null, [Validators.min(1), Validators.max(9999.99)]],
    unit: [BdcSizeUnit.SQM, [Validators.required]],
  })

  currentUnitType: BdcUnitMetadataUnitTypeItem
  configurationForm = this.fb.group({
    unit_type_id: [null, [Validators.required]],
    rooms: this.fb.array([this.createRoomForm()]),
  })

  form = this.fb.group({
    unit_name_id: [null, [Validators.required]],
    number_of_units: [1, [Validators.min(0), Validators.max(32000), integer()]],
    smoking_policy: [],
    size: this.sizeForm,
    partner_reference_name: [],
    floor_numbers_located_on: ['', [Validators.pattern(/^\d+(,\d+)*$/)]], // floor numbers, separated by commas
    occupancy: this.occupancyForm,
    extra_beds_configuration: this.extraBedsForm,
    configuration: this.configurationForm,
  })

  get roomsArray() {
    return this.form.get('configuration.rooms') as FormArray
  }

  maxBedLimit = 3
  maxBedConfigurationLimit = 1

  smokingOptions = bdcSmokingPolicyOptions
  sizeOptions = Object.values(BdcSizeUnit)
  unitNameOptions: BdcUnitMetadataUnitNameItem[] = []
  unitTypeOptions: BdcUnitMetadataUnitTypeItem[] = []
  bdcRoomType = BdcRoomType
  roomTypeOptions = Object.values(BdcRoomType)
  availableRoomTypeOptions = Object.values(BdcRoomType)
  bedTypeOptions: BdcUnitMetadataBedTypeItem[] = []
  sofaTypeOptions: BdcUnitMetadataBedTypeItem[] = []

  isMultiRoom = false
  isSaving = false
  isEdit = false
  constructor(
    private fb: FormBuilder,
    private contentService: BdcContentV2Service,
    private store: Store<any>,
    private toaster: Toaster
  ) {}

  ngOnInit() {
    this.contentService.getUnitMetadata().subscribe((options) => {
      this.unitTypeOptions = options.unit_types
        .filter((item) => item.is_active)
        .filter(
          (item) =>
            item.unit_type_id == this.unit?.configuration?.unit_type_id ||
            item.valid_property_categories?.includes(this.propertyTypeId)
        )
        .sort((a, b) => lodash.toLower(a.unit_type_name).localeCompare(lodash.toLower(b.unit_type_name)))
      this.bedTypeOptions = options.bed_types.filter((item) => item.is_active)
      this.sofaTypeOptions = this.bedTypeOptions.filter((item) => /sofa/i.test(item.bed_type_name))
      this.refreshUnitNameOptions()
      if (this.isEdit) {
        this.currentUnitType = this.unitTypeOptions.find(
          (t) => t.unit_type_id === this.unit?.configuration?.unit_type_id
        )
        this.isMultiRoom = this.currentUnitType?.is_multi_room
        this.refreshRoomTypes(this.isMultiRoom)
      }
    })
  }

  setForm() {
    this.form.patchValue({
      ...this.unit,
      floor_numbers_located_on: this.unit.floor_numbers_located_on?.join(','),
    })
    this.roomsArray.clear()
    this.unit.configuration.rooms
      .map((room) => this.createRoomForm(room, this.isMultiRoom))
      .forEach((room) => this.roomsArray.push(room))
  }

  onUnitTypeChange([type]: BdcUnitMetadataUnitTypeItem[]) {
    this.form.get('unit_name_id').reset()
    this.refreshUnitNameOptions()
    if (type?.is_multi_room != this.isMultiRoom) {
      this.roomsArray.clear()
      this.roomsArray.push(this.createRoomForm(null, type?.is_multi_room))
    }
    this.isMultiRoom = type?.is_multi_room
    this.refreshRoomTypes(this.isMultiRoom)
  }

  refreshRoomTypes(isMultiRoom: boolean) {
    this.maxBedConfigurationLimit = this.isMultiRoom ? 1 : 2
    this.availableRoomTypeOptions = isMultiRoom
      ? [BdcRoomType.BEDROOM_SUBROOM, BdcRoomType.LIVING_ROOM_SUBROOM]
      : [BdcRoomType.GUEST_ROOM]
  }

  refreshUnitNameOptions() {
    const selectedUnitTypeId = this.form.get('configuration.unit_type_id').value as number
    if (!selectedUnitTypeId) return null
    const unitType = this.unitTypeOptions.find((item) => item.unit_type_id === selectedUnitTypeId)
    this.unitNameOptions = lodash.sortBy(unitType?.suggested_unit_names || [], (t) => lodash.toLower(t.unit_name))
  }

  createRoomForm(room?: IBdcRoom, isMultiRoom?: boolean) {
    const form = this.fb.group({
      type: [room?.type || (isMultiRoom ? null : BdcRoomType.GUEST_ROOM), [Validators.required]],
      bed_configurations: this.fb.array([]),
    })

    if (isSomething(room?.bed_configurations)) {
      // default first
      const bedConfigurations = lodash
        .sortBy(room.bed_configurations, (c) => !c.is_default_configuration)
        .map((config) => this.createBedConfigurationForm(config, room?.type))
      form.setControl('bed_configurations', this.fb.array(bedConfigurations))
    } else if (!room) {
      // first configure should be default
      form.setControl('bed_configurations', this.fb.array([this.createBedConfigurationForm(null, room?.type, true)]))
    }

    return form
  }

  onAddRoom() {
    this.roomsArray.push(this.createRoomForm(null, this.isMultiRoom))
  }

  onRoomTypeChange(event: MatSelectChange, room: FormGroup) {
    const roomType = event.value
    const bedConfigurations = room.get('bed_configurations') as FormArray
    if (!this.isBedRequired(roomType)) {
      bedConfigurations.clear()
    }
  }

  isBedRequired(roomType: BdcRoomType) {
    return !this.isLivingRoom(roomType)
  }

  isLivingRoom(roomType: BdcRoomType) {
    return roomType === BdcRoomType.LIVING_ROOM_SUBROOM
  }

  createBedConfigurationForm(config?: IBdcBedConfiguration, roomType?: BdcRoomType, isDefault = false) {
    const form = this.fb.group({
      is_default_configuration: [config?.is_default_configuration ?? isDefault],
      beds: this.fb.array([]),
    })
    if (isSomething(config?.beds)) {
      const beds = config.beds.map((bed) => this.createBedForm(bed))
      form.setControl('beds', this.fb.array(beds))
    } else {
      form.setControl('beds', this.fb.array(this.isBedRequired(roomType) ? [this.createBedForm()] : []))
    }

    return form
  }

  onAddBedConfig(formArray: FormArray, defaultCfg = false) {
    formArray.push(this.createBedConfigurationForm(null, null, defaultCfg))
  }

  createBedForm(bed?: IBdcBed) {
    return this.fb.group({
      bed_type_id: [bed?.bed_type_id, [Validators.required]],
      bed_count: [bed?.bed_count, [Validators.required, Validators.min(1), Validators.max(255), integer()]],
    })
  }

  onAddBed(formArray: FormArray) {
    formArray.push(this.createBedForm())
  }

  onDeleteItem(formArray: FormArray, index: number) {
    formArray.removeAt(index)
  }

  getBedsArray(room: FormGroup, index: number) {
    return room.get(['bed_configurations', index, 'beds']) as FormArray
  }

  validateUnitData(data: BdcCreateUnitPayload) {
    // https://developers.booking.com/connectivity/docs/rooms-api/rooms-api-validations
    const rooms = data.configuration?.rooms
    if (!isSomething(rooms)) {
      this.toaster.error($localize`At least 1 room should be added`)
      return false
    }
    const unitType = this.unitTypeOptions.find((t) => t.unit_type_id === data.configuration.unit_type_id)
    const isMultiRoom = unitType?.is_multi_room
    const isMonoRoom = isMultiRoom === false
    if (isMultiRoom === true) {
      if (rooms.find((t) => t.type === BdcRoomType.GUEST_ROOM)) {
        this.toaster.error($localize`Room type is not allowed for selected (multi-room) unit type`)
        return false
      }
      if (!rooms.find((t) => t.type === BdcRoomType.BEDROOM_SUBROOM)) {
        this.toaster.error(
          $localize`You need to add at least one ‘Bedroom Subroom’ for the selected multi-room unit type.`
        )
        return false
      }
      if (lodash.sumBy(rooms, (t) => (t.type === BdcRoomType.LIVING_ROOM_SUBROOM ? 1 : 0)) > 5) {
        this.toaster.error($localize`Number of Living Room Subroom rooms must be not greater than 5`)
        return false
      }

      if (lodash.sumBy(rooms, (t) => (t.type === BdcRoomType.BEDROOM_SUBROOM ? 1 : 0)) > 20) {
        this.toaster.error($localize`Number of Bedroom Subroom rooms must be not greater than 20`)
        return false
      }
    }
    if (isMonoRoom === true) {
      if (rooms.find((t) => t.type !== BdcRoomType.GUEST_ROOM)) {
        this.toaster.error($localize`Room type should be Guest Room for selected (mono-room) unit type`)
        return false
      }
      if (rooms.length > 1) {
        this.toaster.error($localize`Mono-room unit type must contain exactly one Guest Room`)
        return false
      }
    }

    // Room type GUEST_ROOM must have at least 1 bed configuration
    const isGuestRoomMissing = lodash
      .filter(rooms, (r) => r.type === BdcRoomType.GUEST_ROOM)
      .find((t) => !t.bed_configurations?.length)
    if (isSomething(isGuestRoomMissing)) {
      this.toaster.error($localize`Guest Room must have at least one bed configuration`)
      return false
    }

    // Room type BEDROOM_SUBROOM must have exactly 1 bed configuration;
    const isBedroomSubroomMissing = lodash
      .filter(rooms, (r) => r.type === BdcRoomType.BEDROOM_SUBROOM)
      .find((t) => t.bed_configurations?.length !== 1)
    if (isSomething(isBedroomSubroomMissing)) {
      this.toaster.error($localize`Bedroom Subroom must have exactly one bed configuration`)
      return false
    }

    // room type LIVING_ROOM_SUBROOM can not have more than 1 bed configuration
    const isLivingRoomSubroomMoreThanOne = lodash
      .filter(rooms, (r) => r.type === BdcRoomType.LIVING_ROOM_SUBROOM)
      .find((t) => t.bed_configurations?.length > 1)
    if (isSomething(isLivingRoomSubroomMoreThanOne)) {
      this.toaster.error($localize`Living Room Subroom can not have more than one bed configuration`)
      return false
    }

    // Number of living rooms must be not greater than 5
    const isLivingRoomMoreThanFive = lodash.filter(rooms, (r) => r.type === BdcRoomType.LIVING_ROOM_SUBROOM).length > 5
    if (isLivingRoomMoreThanFive) {
      this.toaster.error($localize`Number of living rooms must be not greater than 5`)
      return false
    }

    // Number of bedrooms must be not greater than 20
    const isBedroomMoreThanTwenty = lodash.filter(rooms, (r) => r.type === BdcRoomType.BEDROOM_SUBROOM).length > 20
    if (isBedroomMoreThanTwenty) {
      this.toaster.error($localize`Number of bedrooms must be not greater than 20`)
      return false
    }

    const defaultCfgIsNotOne = lodash
      .filter(rooms, (r) => r.bed_configurations?.length > 0)
      .filter((r) => lodash.filter(r.bed_configurations, (b) => b.is_default_configuration).length !== 1)
    if (isSomething(defaultCfgIsNotOne)) {
      this.toaster.error($localize`Each room must have exactly one default bed configuration`)
      return false
    }

    const isBedTypeDuplicated = lodash.find(rooms, (r) =>
      lodash.find(
        r.bed_configurations,
        (c) => c.beds?.length > 0 && lodash.uniqBy(c.beds, (b) => b.bed_type_id).length !== c.beds?.length
      )
    )
    if (isSomething(isBedTypeDuplicated)) {
      this.toaster.error($localize`Bed types should not be repeated within a single bed configuration`)
      return false
    }
    return true
  }

  create() {
    const { floor_numbers_located_on, ...data } = this.form.getRawValue()
    const payload: BdcCreateUnitPayload = compactObject({
      ...data,
      floor_numbers_located_on: floor_numbers_located_on?.split(',').map(Number),
    })
    if (!this.validateUnitData(payload)) return
    this.isSaving = true
    this.contentService
      .createPropertyUnit(this.propertyId, payload)
      .pipe(finalize(() => (this.isSaving = false)))
      .subscribe(
        () => {
          this.toaster.success($localize`Unit created successfully`)
          this.close.emit()
        },
        (error) => {
          this.store.dispatch(ActionFailed({ error }))
        }
      )
  }

  update() {
    const { floor_numbers_located_on, ...data } = this.form.getRawValue()
    const payload = compactObject({
      ...data,
      floor_numbers_located_on: floor_numbers_located_on?.split(',').map(Number),
    })
    if (!this.validateUnitData(payload)) return
    this.isSaving = true
    this.contentService
      .updatePropertyUnit(this.propertyId, this.unit.unit_id, payload)
      .pipe(finalize(() => (this.isSaving = false)))
      .subscribe(
        () => {
          this.toaster.success($localize`Unit updated successfully`)
        },
        (error) => {
          this.store.dispatch(
            ActionFailed({ error: lodash.capitalize(lodash.lowerCase(this.toaster.getError(error))) })
          )
        }
      )
  }

  save() {
    this.onSave(this.form)
  }

  @SaveForm()
  onSave(form: FormGroup) {
    if (this.isEdit) {
      this.update()
    } else {
      this.create()
    }
  }
}
